import React from "react";

const PostPageSkeleton = () => {
  return (
    <>
      {/* Hero section skeleton */}
      <div className="relative mt-16 md:mt-12 lg:mt-16">
        <div className="relative overflow-hidden py-4 md:py-8">
          {/* Background skeleton - matches the actual background image */}
          <div className="absolute inset-0 z-0 h-full w-full animate-pulse bg-gray-200 opacity-15"></div>

          <div className="container relative z-10 mx-auto px-8 py-4">
            <div className="ipad-mr-blog-1 flex flex-col items-center lg:flex-row lg:gap-8 xl:justify-between xl:gap-12">
              {/* Image skeleton - matches main-blog-details-ipad class */}
              <div className="main-blog-details-ipad md:mb-10 lg:mb-0">
                <div className="flex items-center justify-center overflow-hidden rounded-2xl">
                  <div className="h-[400px] w-[600px] animate-pulse rounded-2xl bg-gray-200 lg:shadow-2xl"></div>
                </div>
              </div>

              {/* Content skeleton - matches block-details class */}
              <div className="main-blog-details-ipad block-details gap-2">
                {/* Title and badge skeleton */}
                <div className="mb-4">
                  <div className="mb-2 h-8 w-full animate-pulse rounded bg-gray-300 md:h-10"></div>
                  <div className="h-6 w-32 animate-pulse rounded-full bg-gray-300"></div>
                </div>

                {/* Meta information skeleton - matches actual layout */}
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-8 lg:gap-4">
                    <div className="h-4 w-24 animate-pulse rounded bg-gray-300"></div>
                    <div className="h-4 w-1 animate-pulse rounded bg-gray-300"></div>
                    <div className="h-4 w-32 animate-pulse rounded bg-gray-300"></div>
                  </div>
                </div>

                {/* Action buttons skeleton - matches actual button and social media layout */}
                <div className="flex flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-between md:gap-0 xl:pt-6">
                  <div className="items-baseline justify-between lg:flex">
                    <div className="flex items-center gap-4">
                      <div className="h-8 w-8 animate-pulse rounded bg-gray-300"></div>
                      <div className="h-8 w-8 animate-pulse rounded bg-gray-300"></div>
                      <div className="h-8 w-8 animate-pulse rounded bg-gray-300"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="bg-white">
          <div className="container mx-auto px-8 py-16">
            <div className="flex gap-4 md:gap-12">
              {/* Main content skeleton - matches main-bd-wpr class */}
              <div className="main-bd-wpr w-full gap-4 pb-10 sm:pb-0 lg:w-[75%]">
                {/* AI Summary skeleton - matches content-shown-summary class */}
                <div className="content-shown-summary relative mb-8 gap-2 overflow-hidden rounded-lg p-2 pt-2 md:p-6 md:pt-5">
                  <div className="absolute inset-0 z-0 h-full w-full animate-pulse bg-gray-100 opacity-15"></div>
                  <div className="relative z-10">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="h-6 w-6 animate-pulse rounded bg-gray-300 md:h-8 md:w-8"></div>
                      <div className="h-6 w-40 animate-pulse rounded bg-gray-300 text-xl font-semibold"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-2/3 animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-5/6 animate-pulse rounded bg-gray-300"></div>
                    </div>
                  </div>
                </div>

                {/* Article content skeleton - matches content-shown class */}
                <div className="content-shown space-y-4 text-base text-black">
                  {Array.from({ length: 12 }).map((_, index) => (
                    <div key={index} className="space-y-2">
                      <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                      <div className="h-4 w-4/5 animate-pulse rounded bg-gray-300"></div>
                    </div>
                  ))}
                </div>

                {/* Conclusion skeleton - matches content-shown-conclusion class */}
                <div className="content-shown-conclusion mt-8 gap-2 rounded-lg bg-gray-100 p-6 pt-5">
                  <div className="mb-4 h-8 w-32 animate-pulse rounded bg-gray-300 text-xl font-semibold"></div>
                  <div className="space-y-2">
                    <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                    <div className="h-4 w-full animate-pulse rounded bg-gray-300"></div>
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300"></div>
                    <div className="h-4 w-5/6 animate-pulse rounded bg-gray-300"></div>
                  </div>
                </div>

                {/* Tags skeleton - matches tag-details class */}
                <div className="tag-details mt-8">
                  <div className="mb-5 h-6 w-16 animate-pulse rounded bg-gray-300 text-2xl font-medium"></div>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-8 w-20 animate-pulse rounded-full bg-gray-300"
                        style={{ backgroundColor: "rgb(251, 196, 171)" }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar skeleton - matches main-blmain blog-detail-table class */}
              <div className="main-blmain blog-detail-table sticky top-20 hidden h-[fit-content] overflow-y-auto px-0 lg:block lg:h-[100vh] lg:w-[25%]">
                <div
                  className="Detail--Page--list rounded-md border border-gray-300 p-4 pb-10 text-[#6A707C] sm:pb-4"
                  style={{
                    background:
                      "linear-gradient(180deg, #ebf8f6, #e5e8f2, #f7e6e6)",
                  }}
                >
                  {/* Table of contents title */}
                  <div className="mb-5 h-6 w-40 animate-pulse rounded bg-gray-300 text-[22px] font-semibold"></div>

                  {/* Summary link skeleton */}
                  <div className="mb-2 px-2">
                    <div className="h-4 w-16 animate-pulse rounded bg-gray-300"></div>
                  </div>

                  {/* Main content sections skeleton */}
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={index} className="space-y-1">
                        {/* Main section with expand/collapse button */}
                        <div className="flex items-center justify-between">
                          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-300"></div>
                          <div className="h-4 w-4 animate-pulse rounded bg-gray-300"></div>
                        </div>

                        {/* Sub-items skeleton (showing for some sections) */}
                        {index % 2 === 0 && (
                          <div className="ml-2 mt-2 space-y-1">
                            <div className="h-3 w-2/3 animate-pulse rounded bg-gray-300"></div>
                            <div className="h-3 w-1/2 animate-pulse rounded bg-gray-300"></div>
                            <div className="h-3 w-3/4 animate-pulse rounded bg-gray-300"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Conclusion link skeleton */}
                  <div className="mb-4 mt-2 p-2">
                    <div className="h-4 w-20 animate-pulse rounded bg-gray-300"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PostPageSkeleton;
